/** Organisation Directory — /directory/orgs
 *
 *  Discovery for production ecosystem organisations:
 *  Production Houses · Major Studios · Infrastructure Studios ·
 *  Trade Orgs · Agencies · Vendors · Event Production.
 *
 *  Every signed-in user can BROWSE, but the "Message" chip is
 *  policy-gated via /orgs/:id/can-approach — the chip renders as
 *  either "Message Front Desk" (allowed), "Only members can DM"
 *  (association-locked), or "Route via PH" (senior-role gated).
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Building2, MapPin, Mail, Lock, Film, Radio, ShieldCheck,
  ExternalLink, Phone, Users, Search, X, BadgeCheck, Sparkles,
} from "lucide-react";

const BUCKET_LABELS = {
  production_house:   { label: "Production Houses",       icon: Film },
  studio_major:       { label: "Major Studios / Networks",icon: Building2 },
  studio_infra:       { label: "Studio Floors · Sound · Dubbing · Post", icon: Radio },
  trade_org:          { label: "Trade Organisations",     icon: ShieldCheck },
  agency:             { label: "Advertising · PR · Casting", icon: Users },
  event:              { label: "Event Production",        icon: Users },
  vendor:             { label: "Vendors · Suppliers",     icon: Building2 },
  distributor:        { label: "Distributors",            icon: Users },
  exhibitor:          { label: "Cinema Chains · Exhibitors", icon: Film },
};

const CLAIM_LABELS = {
  claim_initiated:     "Ownership claim in progress",
  claim_pending_kyc:   "OTP verified · KYC pending",
  claim_pending_admin: "KYC submitted · awaiting Call·Sheet review",
};

const PAGE_SIZE = 30;

export default function DirectoryOrgs() {
  const nav = useNavigate();
  const [bucket, setBucket]   = useState("production_house");
  const [q, setQ]             = useState("");
  const [city, setCity]       = useState("");
  const [items, setItems]     = useState([]);
  const [total, setTotal]     = useState(0);
  const [loading, setLoading] = useState(false);
  const [facets, setFacets]   = useState({ buckets: [], cities: [] });
  const [skip, setSkip]       = useState(0);

  const reload = async () => {
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, skip, type: bucket };
      if (q.trim()) params.q = q.trim();
      if (city)     params.city = city;
      const { data } = await api.get("/directory/orgs", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed");
    } finally { setLoading(false); }
  };

  const loadFacets = async () => {
    try {
      const { data } = await api.get("/directory/orgs/facets");
      setFacets(data);
    } catch { /* facets are non-critical */ }
  };

  useEffect(() => { void loadFacets(); }, []);
  useEffect(() => { void reload(); }, [bucket, city, skip]);

  const bucketCount = useMemo(() => {
    const m = {};
    (facets.buckets || []).forEach((b) => { m[b.key] = b.count; });
    return m;
  }, [facets]);

  const clearFilters = () => { setQ(""); setCity(""); setSkip(0); void reload(); };

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10" data-testid="directory-orgs-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-white/50">Ecosystem</div>
          <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tight mt-2">
            Organisation Directory
          </h1>
          <p className="mt-3 text-sm text-white/55 max-w-2xl">
            Every studio floor, production house, trade org, and vendor
            on Call Sheet — searchable and mappable. Contact policy is
            enforced per bucket.
          </p>
        </div>
        <Link to="/directory"
              className="text-[10px] tracking-[0.25em] uppercase text-white/60 hover:text-white"
              data-testid="link-back-to-people-directory">
          ← People Directory
        </Link>
      </div>

      {/* Bucket tabs */}
      <div className="mt-8 flex flex-wrap gap-2" data-testid="bucket-tabs">
        {Object.entries(BUCKET_LABELS).map(([key, { label, icon: Icon }]) => {
          const active = key === bucket;
          return (
            <button
              key={key}
              type="button"
              onClick={() => { setBucket(key); setSkip(0); setQ(""); setCity(""); }}
              className={"flex items-center gap-2 px-4 h-10 rounded-full border text-[11px] uppercase tracking-[0.2em] transition "
                       + (active
                          ? "bg-white text-black border-white"
                          : "border-white/15 text-white/70 hover:text-white hover:border-white/40")}
              data-testid={`bucket-tab-${key}`}
            >
              <Icon size={13}/>
              <span>{label}</span>
              {typeof bucketCount[key] === "number" && (
                <span className={"text-[9px] tabular-nums opacity-70"}>{bucketCount[key]}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Filters */}
      <div className="mt-6 flex gap-3 flex-wrap items-center" data-testid="org-directory-filters">
        <div className="relative flex-1 min-w-[280px]">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40"/>
          <Input value={q} onChange={(e) => setQ(e.target.value)}
                 onKeyDown={(e) => { if (e.key === "Enter") { setSkip(0); void reload(); } }}
                 placeholder="Search brand, legal name…"
                 className="pl-9 bg-black/40 border-white/15"
                 data-testid="org-directory-search"/>
        </div>
        <select value={city} onChange={(e) => { setCity(e.target.value); setSkip(0); }}
                className="bg-black/40 border border-white/15 rounded-md px-3 h-10 text-sm text-white/85"
                data-testid="org-directory-city">
          <option value="">All cities</option>
          {(facets.cities || []).map((c) => (
            <option key={c.key} value={c.key}>{c.key} ({c.count})</option>
          ))}
        </select>
        {(q || city) && (
          <Button variant="ghost" onClick={clearFilters}
                  className="text-white/60"
                  data-testid="org-directory-clear">
            <X size={14}/> Clear
          </Button>
        )}
        <div className="ml-auto text-[10px] tracking-[0.22em] uppercase text-white/45">
          {loading ? "Loading…" : `${total} results`}
        </div>
      </div>

      {/* Cards */}
      <div className="mt-6 grid gap-3" data-testid="org-directory-cards">
        {items.length === 0 && !loading && (
          <div className="text-sm text-white/45 border border-white/10 rounded-xl p-10 text-center">
            No organisations match your filters.
          </div>
        )}
        {items.map((o) => <OrgCard key={o.id} org={o} onOpenMailbox={() => nav(`/orgs/${o.id}/inbox`)}/>)}
      </div>

      {/* Pagination */}
      {total > PAGE_SIZE && (
        <div className="mt-6 flex items-center justify-between">
          <Button variant="ghost" disabled={skip === 0}
                  onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))}
                  className="text-white/70" data-testid="org-directory-prev">
            ← Prev
          </Button>
          <div className="text-[10px] tracking-[0.22em] uppercase text-white/45">
            {skip + 1} — {Math.min(skip + PAGE_SIZE, total)} of {total}
          </div>
          <Button variant="ghost" disabled={skip + PAGE_SIZE >= total}
                  onClick={() => setSkip(skip + PAGE_SIZE)}
                  className="text-white/70" data-testid="org-directory-next">
            Next →
          </Button>
        </div>
      )}
    </div>
  );
}


function OrgCard({ org, onOpenMailbox }) {
  const [approach, setApproach] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get(`/orgs/${org.id}/can-approach`)
       .then((r) => { if (alive) setApproach(r.data); })
       .catch(() => { /* silent */ });
    return () => { alive = false; };
  }, [org.id]);

  const openMailbox = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/mailbox/open", {
        target_org_id: org.id,
        subject: `Hi ${org.brand_name}`,
      });
      if (data.warned) {
        toast.warning(data.warning_message || "Mass-messaging warning issued.");
      } else {
        toast.success(`Mailbox thread opened with ${org.brand_name}.`);
      }
      onOpenMailbox?.(data.thread?.id);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (typeof detail === "object" && detail?.message) {
        toast.error(detail.message);
      } else {
        toast.error(formatApiErrorDetail(detail) || "Could not open mailbox.");
      }
    } finally { setBusy(false); }
  };

  const bucket   = org.bucket;
  const allowed  = approach?.allowed;
  const reason   = approach?.reason;

  return (
    <div className="cs-card rounded-2xl p-5 flex flex-wrap items-start gap-4"
         data-testid={`org-card-${org.id}`}>
      <div className="w-14 h-14 rounded-xl bg-white/[0.06] flex items-center justify-center overflow-hidden shrink-0">
        {org.logo_url
          ? <img src={org.logo_url} alt="" className="w-full h-full object-cover"/>
          : <Building2 size={22} className="text-white/50"/>}
      </div>
      <div className="flex-1 min-w-[200px]">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="font-display text-lg truncate">{org.brand_name || "(unnamed)"}</div>
          {org.verified && <ShieldCheck size={14} className="text-emerald-400" title="Verified"/>}
          {org.is_trade_union && org.claim_status && ["claim_initiated", "claim_pending_kyc", "claim_pending_admin"].includes(org.claim_status) && (
            <Link to={`/trade-union/${org.slug}`}
                  onClick={(e) => e.stopPropagation()}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-sky-500/50 bg-sky-500/10 text-sky-300 text-[10px] tracking-[0.16em] uppercase font-semibold hover:bg-sky-500/20 transition"
                  data-testid={`org-card-onboarding-${org.id}`}
                  title={CLAIM_LABELS[org.claim_status] || "Onboarding in progress"}>
              <Sparkles size={10}/> Onboarding
            </Link>
          )}
          {org.is_trade_union && org.is_ghost && (!org.claim_status || org.claim_status === "unclaimed") && (
            <Link to={`/trade-union/${org.slug}`}
                  onClick={(e) => e.stopPropagation()}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-amber-500/50 bg-amber-500/10 text-amber-300 text-[10px] tracking-[0.16em] uppercase font-semibold hover:bg-amber-500/20 transition"
                  data-testid={`org-card-unclaimed-${org.id}`}>
              Unclaimed · Claim
            </Link>
          )}
          {org.is_trade_union && org.claim_status === "active" && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-emerald-500/50 bg-emerald-500/10 text-emerald-300 text-[10px] tracking-[0.06em]"
                  data-testid={`org-card-claimed-${org.id}`}>
              <BadgeCheck size={10}/> Verified · Active
            </span>
          )}
        </div>
        <div className="text-[10px] tracking-[0.22em] uppercase text-white/50 mt-0.5">
          {(BUCKET_LABELS[bucket]?.label) || bucket}
          {(org.studio_types || []).length > 0 && (
            <span className="ml-2">· {org.studio_types.join(" · ")}</span>
          )}
        </div>
        {(org.city || org.state) && (
          <div className="mt-1.5 text-xs text-white/60 flex items-center gap-1.5">
            <MapPin size={12}/> {[org.city, org.state].filter(Boolean).join(", ")}
            {org.google_maps && (
              <a href={org.google_maps} target="_blank" rel="noreferrer"
                 className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-white/50 hover:text-white">
                Map <ExternalLink size={10}/>
              </a>
            )}
          </div>
        )}
        {/* Contact leak — infra studios only */}
        {org.bucket === "studio_infra" && (org.front_desk_phone || org.front_desk_email) && (
          <div className="mt-2 flex gap-3 flex-wrap text-[11px] text-white/65">
            {org.front_desk_phone && (
              <span className="inline-flex items-center gap-1.5">
                <Phone size={11}/> {org.front_desk_phone}
              </span>
            )}
            {org.front_desk_email && (
              <span className="inline-flex items-center gap-1.5">
                <Mail size={11}/> {org.front_desk_email}
              </span>
            )}
          </div>
        )}
        <OrgVouchChips vouches={org.vouches} testidPrefix={`org-vouch-${org.id}`} />
      </div>
      <div className="flex flex-col items-end gap-2 min-w-[180px]">
        {approach && !allowed && (
          <div className="text-[10px] tracking-[0.2em] uppercase text-amber-300/85 text-right leading-snug max-w-[180px]"
               data-testid={`org-card-locked-${org.id}`}>
            <Lock size={11} className="inline mr-1 -mt-0.5"/>
            {reason === "not_permitted_association" && "Members-only DMs"}
            {reason === "not_permitted_ph_major"     && "Route via Producer"}
            {reason === "not_permitted_post_studio"  && "Post-prod roles only"}
            {!reason && "Locked"}
          </div>
        )}
        {approach && allowed && (
          <Button onClick={openMailbox} disabled={busy}
                  className="btn-themed h-9 rounded-full px-4 text-[11px] uppercase tracking-[0.22em]"
                  data-testid={`org-card-message-${org.id}`}>
            <Mail size={13}/> Message
          </Button>
        )}
      </div>
    </div>
  );
}

/** Verified-by-<Trade Org> chip strip for org cards. Same design
 *  language as the People-Directory version. Each chip is a clickable
 *  deep-link to the vouching trade org's public page — agency handle
 *  when available, directory fallback otherwise.
 */
function OrgVouchChips({ vouches, testidPrefix }) {
  if (!Array.isArray(vouches) || vouches.length === 0) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-1.5" data-testid={testidPrefix}>
      {vouches.map((v) => {
        const href = v.org_handle
          ? `/agency/${v.org_handle}`
          : `/directory/orgs?type=trade_org&q=${encodeURIComponent(v.org_name || "")}`;
        return (
          <Link
            key={v.org_id}
            to={href}
            onClick={(e) => e.stopPropagation()}
            data-testid={`${testidPrefix}-${v.org_id}`}
            title={`Verified by ${v.org_name}`}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-emerald-500/50 bg-emerald-500/10 text-emerald-300 text-[10px] tracking-[0.06em] hover:bg-emerald-500/20 hover:border-emerald-400 transition"
          >
            <BadgeCheck size={10} />
            <span className="truncate max-w-[160px]">Verified by {v.org_name}</span>
          </Link>
        );
      })}
    </div>
  );
}
